import React from "react";
import { Baby, BookOpen, School, GraduationCap, Building2 } from "lucide-react";

const LEVEL_CARDS = [
  {
    key: "mam_non",
    schoolLevel: "Mầm non",
    label: "Mầm non",
    shortLabel: "MN",
    icon: Baby,
    valueClass: "text-rose-900",
    labelClass: "text-rose-800",
    iconWrapClass: "border border-rose-300 bg-white text-rose-800 shadow-sm",
    mobileIconClass: "text-rose-700",
    cardClass:
      "border-rose-300 bg-gradient-to-br from-rose-200 via-rose-100 to-pink-50 shadow-rose-200/60",
    blobClass: "bg-rose-400/55",
    watermarkClass: "text-rose-600/40",
    activeRing: "ring-2 ring-rose-400/80",
  },
  {
    key: "tieu_hoc",
    schoolLevel: "Tiểu học",
    label: "Tiểu học",
    shortLabel: "TH",
    icon: BookOpen,
    valueClass: "text-sky-900",
    labelClass: "text-sky-800",
    iconWrapClass: "border border-sky-300 bg-white text-sky-800 shadow-sm",
    mobileIconClass: "text-sky-700",
    cardClass:
      "border-sky-300 bg-gradient-to-br from-sky-200 via-sky-100 to-cyan-50 shadow-sky-200/60",
    blobClass: "bg-sky-400/55",
    watermarkClass: "text-sky-600/40",
    activeRing: "ring-2 ring-sky-400/80",
  },
  {
    key: "thcs",
    schoolLevel: "THCS",
    label: "THCS",
    shortLabel: "THCS",
    icon: School,
    valueClass: "text-amber-900",
    labelClass: "text-amber-800",
    iconWrapClass: "border border-amber-300 bg-white text-amber-800 shadow-sm",
    mobileIconClass: "text-amber-700",
    cardClass:
      "border-amber-300 bg-gradient-to-br from-amber-200 via-amber-100 to-orange-50 shadow-amber-200/60",
    blobClass: "bg-amber-400/55",
    watermarkClass: "text-amber-600/40",
    activeRing: "ring-2 ring-amber-400/80",
  },
  {
    key: "thpt",
    schoolLevel: "THPT",
    label: "THPT",
    shortLabel: "THPT",
    icon: GraduationCap,
    valueClass: "text-emerald-900",
    labelClass: "text-emerald-800",
    iconWrapClass:
      "border border-emerald-300 bg-white text-emerald-800 shadow-sm",
    mobileIconClass: "text-emerald-700",
    cardClass:
      "border-emerald-300 bg-gradient-to-br from-emerald-200 via-emerald-100 to-teal-50 shadow-emerald-200/60",
    blobClass: "bg-emerald-400/55",
    watermarkClass: "text-emerald-600/40",
    activeRing: "ring-2 ring-emerald-400/80",
  },
];

const defaultStats = {
  total: 0,
  school_count: 0,
  mam_non: 0,
  tieu_hoc: 0,
  thcs: 0,
  thpt: 0,
  chua_phan_loai: 0,
};

/**
 * Mini dashboard GV đối tác theo cấp học của trường.
 * Click card → lọc theo cấp; click lại → bỏ lọc.
 *
 * Visual: Design.md §1.7 (CustomerMiniDashboard).
 */
const TeacherAccountMiniDashboard = ({
  stats = defaultStats,
  selectedLevel = "",
  onSelectLevel,
  loading = false,
}) => {
  const s = { ...defaultStats, ...stats };

  const handleClick = (schoolLevel) => {
    if (!onSelectLevel) return;
    onSelectLevel(selectedLevel === schoolLevel ? "" : schoolLevel);
  };

  return (
    <section className="mb-4 md:mb-6" aria-label="Thống kê theo cấp học">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-700">
          <Building2 className="h-3.5 w-3.5 text-red-600" />
          {loading ? "–" : Number(s.school_count || 0).toLocaleString("vi-VN")} trường
        </span>
        <span className="text-gray-300">·</span>
        <span>
          {loading ? "–" : Number(s.total || 0).toLocaleString("vi-VN")} giáo viên
        </span>
        {s.chua_phan_loai > 0 ? (
          <>
            <span className="text-gray-300">·</span>
            <span className="text-amber-700">
              {Number(s.chua_phan_loai).toLocaleString("vi-VN")} chưa phân loại cấp học
            </span>
          </>
        ) : null}
      </div>

      {/* Mobile pill bar */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-1 overflow-hidden rounded-full border border-gray-100 bg-white p-1 shadow-sm">
          {LEVEL_CARDS.map((card) => {
            const Icon = card.icon;
            const active = selectedLevel === card.schoolLevel;
            return (
              <button
                key={card.key}
                type="button"
                title={card.label}
                onClick={() => handleClick(card.schoolLevel)}
                className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-1.5 py-2.5 transition-colors sm:gap-2 sm:px-2 ${
                  active ? "bg-red-50 ring-1 ring-red-200" : "hover:bg-gray-50"
                }`}
              >
                <Icon
                  className={`h-[18px] w-[18px] shrink-0 sm:h-5 sm:w-5 ${card.mobileIconClass}`}
                  strokeWidth={2.25}
                  aria-hidden
                />
                <span className="text-base font-bold leading-none text-gray-700 tabular-nums">
                  {loading ? "–" : s[card.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop cards — Design.md §1.7 */}
      <div className="hidden grid-cols-1 gap-4 md:grid sm:grid-cols-2 xl:grid-cols-4">
        {LEVEL_CARDS.map((card) => {
          const Icon = card.icon;
          const active = selectedLevel === card.schoolLevel;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => handleClick(card.schoolLevel)}
              className={`relative overflow-hidden rounded-2xl border p-4 text-left shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                card.cardClass
              } ${active ? card.activeRing : ""}`}
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full blur-2xl ${card.blobClass}`}
              />
              <Icon
                aria-hidden
                className={`pointer-events-none absolute -bottom-3 -right-2 h-24 w-24 ${card.watermarkClass}`}
                strokeWidth={1.25}
              />

              <div className="relative z-10">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className={`text-[11px] font-bold uppercase tracking-wider ${card.labelClass}`}
                    >
                      {card.label}
                    </p>
                    <p
                      className={`mt-3 text-3xl font-black leading-none tabular-nums ${card.valueClass}`}
                    >
                      {loading ? "–" : s[card.key]}
                    </p>
                    <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Giáo viên
                    </p>
                  </div>
                  <div className={`shrink-0 rounded-xl p-2 ${card.iconWrapClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedLevel ? (
        <p className="mt-2 text-[10px] text-gray-400">
          Đang lọc cấp học: <span className="font-bold text-gray-600">{selectedLevel}</span>
          {" — "}
          bấm lại thẻ để bỏ lọc.
        </p>
      ) : (
        <p className="mt-2 hidden text-[10px] text-gray-400 md:block">
          Số liệu theo bộ lọc hiện tại. Bấm thẻ cấp học để lọc danh sách trường.
        </p>
      )}
    </section>
  );
};

export default TeacherAccountMiniDashboard;
export { LEVEL_CARDS };
